import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { recordSharedEvent, useSharedPlan } from '../../api/hooks/useSharing.ts';
import PlanDocument, { PAGE_TITLES } from '../../components/print/PlanDocument.tsx';

/** Public read-only viewer behind a secret share link — no account, no app shell. */
export default function SharedPlan() {
  const { token } = useParams<{ token: string }>();
  const shared = useSharedPlan(token);
  const [page, setPage] = useState<number | undefined>(1);

  const doc = shared.data?.doc;
  const currentSlugs =
    doc && page !== undefined
      ? doc.sections.filter((s) => s.section.page === page).map((s) => s.section.slug)
      : [];
  const slugKey = currentSlugs.join(',');

  // record which sections the reader opened as they navigate pages
  useEffect(() => {
    if (!token || !slugKey) return;
    for (const slug of slugKey.split(',')) recordSharedEvent(token, slug);
  }, [token, slugKey]);

  if (shared.isPending) {
    return <main className="p-8 text-center text-sm text-gray-400">Loading…</main>;
  }
  if (shared.isError || !doc) {
    return (
      <main className="flex min-h-screen items-center justify-center p-8">
        <div className="text-center">
          <h1 className="mb-2 text-xl font-bold text-gray-800">This link is no longer valid</h1>
          <p className="text-sm text-gray-500">
            The share link may have been revoked. Ask the plan owner for a new one.
          </p>
        </div>
      </main>
    );
  }

  const pages = [...new Set(doc.sections.map((s) => s.section.page))].sort((a, b) => a - b);

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="sticky top-0 z-10 border-b border-gray-200 bg-white px-6 py-3 print:hidden">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3">
          <span className="text-sm font-semibold text-gray-700">{doc.plan.title}</span>
          <div className="flex items-center gap-2">
            {pages.map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                title={PAGE_TITLES[p]}
                className={`rounded-full px-3 py-1 text-sm font-medium ${
                  p === page ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage(undefined)}
              className={`rounded-full px-3 py-1 text-sm font-medium ${
                page === undefined ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => window.print()}
              className="ml-2 rounded-md border border-gray-300 px-3 py-1 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Print / PDF
            </button>
          </div>
        </div>
      </nav>
      <div className="py-8 print:py-0">
        <div className="mx-auto max-w-3xl shadow-sm print:max-w-none print:shadow-none">
          <PlanDocument doc={doc} onlyPage={page} />
        </div>
      </div>
    </div>
  );
}
